import React, { useState, useMemo, useLayoutEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Alert,
  Skeleton
} from '@mui/material';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import { ScheduleMatch, scheduleService, DepartureTimeInfo } from '@/services/scheduleService';
import { conflictService } from '@/services/conflictService';
import { playerService } from '@/services/playerService';
import { generateConflictDescription } from '@/utils/conflictFormatter';
import AgendaDialog, { AgendaMatch } from './AgendaDialog';
import { getEffectiveTimezone } from '@/utils/timezoneUtils';
import { RootState } from '@/store';
import { PLAYER_COLORS, getPlayerColorMap } from '@/utils/playerColors';

dayjs.extend(utc);
dayjs.extend(timezone);

interface CalendarEvent {
  id: string;
  title: string;
  start: string; // Changed from date to start for FullCalendar compatibility
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  allDay?: boolean;
  classNames?: string[];
  extendedProps: {
    match: ScheduleMatch;
    playerColor: string;
    hasConflict: boolean;
    isTBD?: boolean;
  };
}

interface FullCalendarDashboardProps {
  filteredMatches?: ScheduleMatch[];
  currentMonth?: string;
  onMonthChange?: (month: string) => void;
  isLoading?: boolean;
}

const FullCalendarDashboard: React.FC<FullCalendarDashboardProps> = ({ 
  filteredMatches, 
  currentMonth: externalCurrentMonth,
  isLoading: externalIsLoading = false
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [agendaOpen, setAgendaOpen] = useState(false);

  // Redux selectors for timezone data
  const user = useSelector((state: RootState) => state.auth.user);
  const orgSettings = useSelector((state: RootState) => state.organization.settings);
  
  const [currentMonth, setCurrentMonth] = useState(externalCurrentMonth || dayjs().format('YYYY-MM'));
  const [calendarApi, setCalendarApi] = useState<any>(null); // Store calendar API reference
  const [scrollPosition, setScrollPosition] = useState<number>(0); // Store scroll position
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update internal month state when external month changes
  React.useEffect(() => {
    if (externalCurrentMonth && externalCurrentMonth !== currentMonth) {
      setCurrentMonth(externalCurrentMonth);
    }
  }, [externalCurrentMonth, currentMonth]);

  // Enable automatic calendar refresh when data changes
  // Remove auto-refresh to prevent unwanted re-renders during navigation
  // useCalendarAutoRefresh();

  // Fetch all players for stable color mapping
  const { data: allPlayers = [] } = useQuery(
    'players',
    () => playerService.getPlayers(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes - players don't change often
      cacheTime: 1000 * 60 * 30, // 30 minutes
    }
  );

  // Fetch schedule data for calendar independently from parent
  const { data: scheduleMatches, isLoading: scheduleLoading, error: scheduleError } = useQuery(
    ['schedule-calendar', currentMonth],
    () => {
      // Use a reasonable date range to match schedule page caching
      const startDate = dayjs().subtract(6, 'month').format('YYYY-MM-DD');
      const endDate = dayjs().add(6, 'month').format('YYYY-MM-DD');
      
      return scheduleService.getSchedule({
        startDate,
        endDate,
        playerIds: [] // Get all players' matches within date range
      });
    },
    {
      enabled: filteredMatches === undefined, // Only fetch if no filtered matches provided
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 15, // 15 minutes
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  // Always prioritize filtered matches from parent over own fetched data
  const calendarScheduleData = useMemo(() => {
    // Always use filteredMatches if provided, regardless of length
    if (filteredMatches !== undefined) {
      return filteredMatches;
    }
    return scheduleMatches || [];
  }, [filteredMatches, scheduleMatches]);
  const { data: conflictData } = useQuery(
    ['conflicts-calendar', currentMonth],
    () => conflictService.getConflicts(),
    {
      staleTime: 1000 * 60 * 3, // 3 minutes - conflicts don't change often
      cacheTime: 1000 * 60 * 15, // 15 minutes - keep conflicts cached longer
      keepPreviousData: true, // Show previous data while loading
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnWindowFocus: true,
    }
  );

  // Create stable player color mapping using all players (stored colors first, then auto-assign)
  const playerColorMap = useMemo(() => {
    if (!allPlayers || allPlayers.length === 0) return {};
    return getPlayerColorMap(allPlayers);
  }, [allPlayers]);

  // Create conflict lookup for individual matches with severity information
  const conflictLookup = useMemo(() => {
    if (!conflictData?.conflictGroups) return new Map<string, { hasConflict: boolean; severity?: string }>();
    
    const conflictMap = new Map<string, { hasConflict: boolean; severity?: string }>();
    conflictData.conflictGroups.forEach(group => {
      group.conflicts.forEach(conflict => {
        conflictMap.set(conflict.match1.match.id, { hasConflict: true, severity: conflict.severity });
        conflictMap.set(conflict.match2.match.id, { hasConflict: true, severity: conflict.severity });
      });
    });
    
    return conflictMap;
  }, [conflictData?.conflictGroups]);

  // Helper function to find conflicts between matches
  const getConflictBetweenMatches = (match1: AgendaMatch, match2: AgendaMatch) => {
    if (!conflictData?.conflictGroups) return null;
    
    // Look for conflicts involving both matches
    for (const group of conflictData.conflictGroups) {
      for (const conflict of group.conflicts) {
        const match1Id = match1.id.split('-')[0]; // Remove player suffix
        const match2Id = match2.id.split('-')[0]; // Remove player suffix
        
        const conflictMatch1Id = conflict.match1.match.id;
        const conflictMatch2Id = conflict.match2.match.id;
        
        // Check if these two matches are in conflict with each other
        if ((conflictMatch1Id === match1Id && conflictMatch2Id === match2Id) ||
            (conflictMatch1Id === match2Id && conflictMatch2Id === match1Id)) {
          return {
            severity: conflict.severity,
            description: generateConflictDescription(conflict),
            timeDifference: conflict.timeConflict?.timeDifference,
            availableTravelWindow: conflict.timeConflict?.availableTravelWindow,
            travelTime: conflict.timeConflict?.travelTime,
            type: conflict.timeConflict?.type
          };
        }
      }
    }
    return null;
  };

  // Get departure time for a specific match (only upcoming matches)
  const getDepartureTimeForMatch = (match: any): DepartureTimeInfo | null => {
    if (!match.players || match.players.length === 0) return null;
    if (!match.departureTimes) return null;
    // Don't show departure time for matches that have already occurred
    if (match.dateTime && dayjs(match.dateTime).add(2, 'hour').isBefore(dayjs())) return null;

    const playerId = match.players[0].id;
    return match.departureTimes[playerId] || null;
  };

  // Transform schedule data into FullCalendar events
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    console.log('=== calendarEvents useMemo triggered ===');
    console.log('calendarScheduleData length:', calendarScheduleData?.length || 0);
    console.log('currentMonth:', currentMonth);
    
    const dataToUse = calendarScheduleData;
    
    if (!dataToUse || dataToUse.length === 0) {
      return [];
    }

    const events: CalendarEvent[] = [];

    dataToUse.forEach((match: any) => {
      
      if (!match.players || match.players.length === 0) {
        return;
      }

      // Skip postponed matches - they won't take place on the scheduled date
      if (match.status === 'POSTPONED') {
        return;
      }

      // Skip matches that have no date AND are not TBD with a scheduled date
      if (!match.dateTime && match.status !== 'TBD') {
        return;
      }

      match.players.forEach((player: any) => {
        const playerColor = playerColorMap[player.name] || PLAYER_COLORS[0];
        const conflictInfo = conflictLookup.get(match.id);
        const hasConflict = conflictInfo?.hasConflict || false;
        
        // Use the isHome property from the player data
        const isHome = player.isHome;
        const opponent = isHome ? match.awayTeam : match.homeTeam;

        // Create title with v/@ prefix (Google Calendar style)
        const vsAt = isHome ? 'v' : '@';
        let title = `${vsAt} ${opponent}`;
        
        // Check if this is a TBD match (midnight UTC time or explicit TBD status)
        let isTBDMatch = match.status === 'TBD';
        if (match.dateTime) {
          const matchTimeUTC = dayjs.utc(match.dateTime);
          const hour = matchTimeUTC.hour();
          const minute = matchTimeUTC.minute();
          
          if ((hour === 0 && minute === 0) || (hour === 0 && minute === 1)) {
            isTBDMatch = true;
          }
        }
        
        // Create title - for TBD matches, show "TBD" instead of time
        if (isTBDMatch) {
          title = `TBD ${title}`;
        }
        
        // Render ALL events as all-day bars (Google Calendar style)
        // Store original dateTime for sorting
        const originalDateTime = match.dateTime;
        let eventStart: string;

        if (isTBDMatch) {
          if (match.dateTime) {
            eventStart = dayjs.utc(match.dateTime).format('YYYY-MM-DD');
          } else {
            // Skip TBD matches with no date at all
            return;
          }
        } else if (match.dateTime) {
          // Use just the date part so events render as all-day bars
          const userTz = getEffectiveTimezone(user, orgSettings);
          eventStart = dayjs.utc(match.dateTime).tz(userTz).format('YYYY-MM-DD');
        } else {
          return;
        }

        // Use proper FullCalendar event format
        // Determine text color based on background color for optimal contrast
        const getTextColor = (bgColor: string) => {
          // Light colors that need dark text
          const lightColors = ['#ECE133', '#FBAFE4', '#56B4E9'];
          return lightColors.includes(bgColor) ? '#000000' : '#ffffff';
        };

        const event = {
          id: `${match.id}-${player.id}`,
          title: title,
          start: eventStart,
          allDay: true,
          backgroundColor: playerColor,
          borderColor: playerColor,
          textColor: getTextColor(playerColor),
          classNames: [
            ...(hasConflict ? ['conflict-indicator'] : []),
            ...(isTBDMatch ? ['tbd-event'] : [])
          ],
          extendedProps: {
            match,
            playerColor,
            hasConflict,
            conflictSeverity: conflictInfo?.severity,
            isTBD: isTBDMatch,
            departureTime: getDepartureTimeForMatch(match),
            originalDateTime: originalDateTime
          }
        };

        events.push(event);
      });
    });

    return events;
  }, [calendarScheduleData, playerColorMap, conflictLookup]);

  // Create filtered player list for legend (only players with matches in current view)
  const playersInCurrentView = useMemo(() => {
    if (!calendarEvents || calendarEvents.length === 0) return [];
    
    const playersInView = new Set<string>();
    calendarEvents.forEach((event: CalendarEvent) => {
      // Extract player name from the event ID (format: matchId-playerId)
      const eventId = event.id;
      if (typeof eventId === 'string' && event.extendedProps?.match) {
        const match = event.extendedProps.match;
        // Find the player from the match data using the event ID
        const player = match.players?.find((p: any) => eventId.includes(p.id));
        if (player?.name) {
          playersInView.add(player.name);
        }
      }
    });
    
    // Return sorted array of player names that have matches in current view
    return Array.from(playersInView).sort();
  }, [calendarEvents]);

  // Get matches for selected date (for agenda dialog)
  const selectedDateMatches: AgendaMatch[] = useMemo(() => {
    if (!selectedDate || !calendarScheduleData) return [];

    const dayMatches: AgendaMatch[] = [];
    
    calendarScheduleData.forEach((match: ScheduleMatch) => {
      // Check if this is a TBD match first
      let isTBDMatch = match.status === 'TBD';
      if (match.dateTime) {
        const matchTimeUTC = dayjs.utc(match.dateTime);
        const hour = matchTimeUTC.hour();
        const minute = matchTimeUTC.minute();
        
        if ((hour === 0 && minute === 0) || (hour === 0 && minute === 1)) {
          isTBDMatch = true;
        }
      }
      
      // Use appropriate timezone for date formatting
      const matchDate = match.dateTime ? 
        (isTBDMatch ? dayjs.utc(match.dateTime).format('YYYY-MM-DD') : dayjs(match.dateTime).format('YYYY-MM-DD')) 
        : null;
      
      if (matchDate === selectedDate) {
        match.players.forEach(player => {
          const playerColor = playerColorMap[player.name] || PLAYER_COLORS[0];
          const conflictInfo = conflictLookup.get(match.id);
          const hasConflict = conflictInfo?.hasConflict || false;
          const departureTime = getDepartureTimeForMatch(match);
          
          // Use the isHome property from the player data
          const isHome = player.isHome;

          dayMatches.push({
            id: `${match.id}-${player.id}`,
            dateTime: match.dateTime,
            homeTeam: match.homeTeam,
            awayTeam: match.awayTeam,
            isHome,
            location: match.location,
            league: match.league.name,
            division: match.league.division,
            player: player.name,
            playerColor,
            hasConflict,
            conflictSeverity: conflictInfo?.severity,
            status: match.status,
            homeTeamLogo: match.homeTeamLogo,
            awayTeamLogo: match.awayTeamLogo,
            departureTime: departureTime || undefined
          });
        });
      }
    });

    // Sort by time
    dayMatches.sort((a, b) => {
      if (!a.dateTime && !b.dateTime) return 0;
      if (!a.dateTime) return 1;
      if (!b.dateTime) return -1;
      return dayjs(a.dateTime).isBefore(dayjs(b.dateTime)) ? -1 : 1;
    });

    // Deduplicate: group by base match ID (strip -playerId suffix)
    const groups = new Map<string, AgendaMatch>();
    for (const m of dayMatches) {
      const baseId = m.id.replace(/-[^-]+$/, '');
      const existing = groups.get(baseId);
      if (existing) {
        const players = existing.players || [{ name: existing.player, color: existing.playerColor, isHome: existing.isHome }];
        players.push({ name: m.player, color: m.playerColor, isHome: m.isHome });
        existing.players = players;
        if (m.hasConflict) existing.hasConflict = true;
      } else {
        groups.set(baseId, { ...m, players: [{ name: m.player, color: m.playerColor, isHome: m.isHome }] });
      }
    }

    return Array.from(groups.values());
  }, [selectedDate, calendarScheduleData, playerColorMap, conflictLookup]);

  // Prevent scroll to top when agenda dialog opens - using useLayoutEffect for immediate execution
  useLayoutEffect(() => {
    if (agendaOpen && scrollPosition > 0) {
      // Restore scroll position immediately when dialog opens
      window.scrollTo(0, scrollPosition);
      
      // Also prevent any subsequent scroll events for a brief moment
      const preventScroll = (e: Event) => {
        e.preventDefault();
        window.scrollTo(0, scrollPosition);
      };
      
      // Add scroll prevention for a very brief moment
      document.addEventListener('scroll', preventScroll, { passive: false });
      
      // Remove the scroll prevention after a short delay
      const timeout = setTimeout(() => {
        document.removeEventListener('scroll', preventScroll);
      }, 100);
      
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('scroll', preventScroll);
      };
    }
  }, [agendaOpen, scrollPosition]);

  // Fetch departure times when players or schedule data changes
  const handleDateClick = (info: any) => {
    // Store current scroll position before opening dialog
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    setScrollPosition(currentScroll);
    
    // Use requestAnimationFrame to ensure scroll position is maintained
    requestAnimationFrame(() => {
      // Use the clicked date directly (FullCalendar provides local date)
      setSelectedDate(info.dateStr);
      setAgendaOpen(true);
      
      // Ensure scroll position is maintained after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, currentScroll);
      });
    });
  };

  const handleEventClick = (info: any) => {
    // Store current scroll position before opening dialog
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    setScrollPosition(currentScroll);
    
    // Use requestAnimationFrame to ensure scroll position is maintained
    requestAnimationFrame(() => {
      // Extract date from the clicked event (use UTC since all-day events store start at UTC midnight)
      const eventDate = dayjs.utc(info.event.start).format('YYYY-MM-DD');
      setSelectedDate(eventDate);
      setAgendaOpen(true);
      
      // Ensure scroll position is maintained after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, currentScroll);
      });
    });
  };

  // Handle month change with proper debouncing
  const handleDatesSet = React.useCallback((info: any) => {
    console.log('=== handleDatesSet called ===');
    console.log('currentMonth before:', currentMonth);
    console.log('info.view.currentStart:', info.view.currentStart);
    
    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      console.log('Cleared existing timeout');
    }
    
    // Store calendar API reference
    if (!calendarApi && info.view.calendar) {
      setCalendarApi(info.view.calendar);
    }
    
    // Get the new month from the calendar view
    const newMonth = dayjs(info.view.currentStart || info.view.calendar.getDate()).format('YYYY-MM');
    console.log('newMonth calculated:', newMonth);
    
    // Debounce the month update
    navigationTimeoutRef.current = setTimeout(() => {
      console.log('Timeout fired - updating month to:', newMonth);
      setCurrentMonth(prevMonth => {
        console.log('setCurrentMonth - prevMonth:', prevMonth, 'newMonth:', newMonth);
        if (newMonth !== prevMonth) {
          console.log('UPDATING month from', prevMonth, 'to', newMonth);
          return newMonth;
        }
        console.log('NO CHANGE - keeping month:', prevMonth);
        return prevMonth;
      });
      navigationTimeoutRef.current = null;
    }, 100); // Short delay to debounce multiple calls
    
  }, [calendarApi, currentMonth]);

  // Close agenda dialog
  const handleCloseAgenda = () => {
    setAgendaOpen(false);
    // Restore scroll position after dialog closes
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
      setSelectedDate(null);
    }, 120);
  };

  if (scheduleError && !filteredMatches) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load calendar data. Please try refreshing the page.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Temporarily move calendar outside Card for testing */}
      <Box sx={{ width: '100%', mb: 2 }}>
        {(scheduleLoading && !filteredMatches) ? (
          <Box py={1}>
            {/* Calendar header skeleton */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={1}>
              <Skeleton variant="text" width={120} height={32} animation="wave" />
              <Box display="flex" gap={1}>
                <Skeleton variant="circular" width={28} height={28} animation="wave" />
                <Skeleton variant="circular" width={28} height={28} animation="wave" />
              </Box>
            </Box>
            {/* Weekday header row */}
            <Box display="flex" mb={1}>
              {['S','M','T','W','T','F','S'].map((_, i) => (
                <Box key={i} flex={1} textAlign="center">
                  <Skeleton variant="text" width={20} height={20} animation="wave" sx={{ mx: 'auto' }} />
                </Box>
              ))}
            </Box>
            {/* 5 weeks of day cells */}
            {Array.from({ length: 5 }).map((_, week) => (
              <Box key={week} display="flex">
                {Array.from({ length: 7 }).map((_, day) => (
                  <Box key={day} flex={1} sx={{ height: 100, border: '1px solid', borderColor: 'divider', p: 0.5 }}>
                    <Skeleton variant="text" width={16} height={16} animation="wave" />
                    {(week + day) % 3 === 0 && (
                      <Skeleton variant="rectangular" width="85%" height={12} animation="wave" sx={{ borderRadius: '3px', mt: 0.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <>
            <div id="calendar">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={(scheduleLoading || externalIsLoading) ? [] : calendarEvents}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                datesSet={handleDatesSet}
                fixedWeekCount={true}
                expandRows={true}
                height="auto"
                timeZone={getEffectiveTimezone(user, orgSettings)}
                navLinks={false}
                eventOrder={(a: any, b: any) => {
                  // Sort by original time (earliest first), TBD events last
                  const aTime = a.extendedProps?.originalDateTime;
                  const bTime = b.extendedProps?.originalDateTime;
                  const aTBD = a.extendedProps?.isTBD;
                  const bTBD = b.extendedProps?.isTBD;
                  if (aTBD && !bTBD) return 1;
                  if (!aTBD && bTBD) return -1;
                  if (!aTime && !bTime) return 0;
                  if (!aTime) return 1;
                  if (!bTime) return -1;
                  return dayjs(aTime).isBefore(dayjs(bTime)) ? -1 : 1;
                }}
              />
            </div>
            
            {/* Players Legend - Bottom Right */}
            {playersInCurrentView.length > 0 && (
              <Box sx={{ 
                mt: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flexShrink: 0, fontSize: '0.8rem' }}>
                  Players:
                </Typography>
                {playersInCurrentView.map((playerName, index) => (
                  <Box
                    key={playerName}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        backgroundColor: playerColorMap[playerName] || PLAYER_COLORS[0],
                        borderRadius: '50%',
                        flexShrink: 0
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                      {playerName}
                    </Typography>
                    {index < playersInCurrentView.length - 1 && (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mx: 0.5 }}>
                        •
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Shared Agenda Dialog */}
      <AgendaDialog
        open={agendaOpen}
        onClose={handleCloseAgenda}
        selectedDate={selectedDate}
        matches={selectedDateMatches}
        getConflictBetweenMatches={getConflictBetweenMatches}
      />

      {/* FullCalendar CSS + uniform cell heights + no scrollbars + full width */}
      <style>
        {`
          /* Remove pulsating animation for conflict indicators */
          .conflict-indicator {
            /* No animation */
          }
          
          /* Basic FullCalendar demo styles - scoped to calendar only */
          #calendar {
            /* Use full width of Card container */
            width: 100%;
            margin: 0;
            /* Apply demo font styles only to calendar */
            font-family: Arial, Helvetica Neue, Helvetica, sans-serif;
            font-size: 14px;
          }
          
          /* Ensure uniform cell heights */
          .fc-daygrid-day {
            height: 120px;
          }
          
          /* Eliminate scrollbars from FullCalendar */
          .fc-scroller {
            overflow: visible !important;
          }
          
          .fc-daygrid-body {
            overflow: visible !important;
          }
          
          .fc-view-harness {
            overflow: visible !important;
          }
          
          /* Prevent event title overflow and allow multi-line */
          .fc-event {
            overflow: hidden !important;
          }
          
          .fc-event-title {
            overflow: hidden !important;
            font-size: 0.75rem !important;
            line-height: 1.1 !important;
          }
          
          /* Ensure proper event padding */
          .fc-event-main {
            padding: 1px 2px !important;
          }
        `}
      </style>
    </>
  );
};

export default FullCalendarDashboard;
